import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import api from '../services/api';
import AppointmentImageManager from './AppointmentImageManager';
import AppointmentImageViewer from './AppointmentImageViewer';
import './TattooProjects.css';

const emptySession = { dayId: '', time: '10:00', duration: '240', minRange: '', maxRange: '', deposit: '', note: '' };
const dateLabel = value => value ? value.split('-').reverse().join('.') : '';

export default function TattooProjectDialog({ appointment, onClose, onChanged, locationId, locations = [], onShare }) {
  const [project, setProject] = useState(null);
  const [title, setTitle] = useState(appointment.project_title || '');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(Boolean(appointment.project_id));
  const [candidates, setCandidates] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showLink, setShowLink] = useState(false);
  const [showSession, setShowSession] = useState(false);
  const [session, setSession] = useState(emptySession);
  const [days, setDays] = useState([]);
  const [targetLocation, setTargetLocation] = useState(String(locationId || ''));
  const [daysLoading, setDaysLoading] = useState(false);
  const [viewerImages, setViewerImages] = useState([]);
  const [currentImage, setCurrentImage] = useState(0);

  const accept = (data, resetFields = true) => {
    setProject(previous => resetFields || !previous ? data : {
      ...data, title: previous.title, description: previous.description, revision: previous.revision,
    });
    if (resetFields) { setTitle(data.title); setDescription(data.description || ''); }
  };

  useEffect(() => {
    if (!appointment.project_id) return;
    const controller = new AbortController();
    api.get(`/api/tattoo-projects/${appointment.project_id}`, { signal: controller.signal })
      .then(response => accept(response.data))
      .catch(err => { if (!controller.signal.aborted) setError(err.response?.data?.message || 'Projekt konnte nicht geladen werden.'); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [appointment.project_id]);

  useEffect(() => {
    if (!showSession || !targetLocation) return;
    const controller = new AbortController();
    setDays([]); setDaysLoading(true); setSession(previous => ({ ...previous, dayId: '' }));
    api.get('/api/available_days_with_artists', { params: { locationId: targetLocation }, signal: controller.signal })
      .then(response => {
        // Browser local day avoids UTC date shifts around midnight.
        const now = new Date();
        const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        setDays(response.data.filter(day => String(day.day).slice(0, 10) >= today)
          .sort((a, b) => String(a.day).localeCompare(String(b.day)) || String(a.artistName).localeCompare(String(b.artistName))));
      })
      .catch(err => { if (!controller.signal.aborted) setError('Verfügbare Tage konnten nicht geladen werden.'); })
      .finally(() => { if (!controller.signal.aborted) setDaysLoading(false); });
    return () => controller.abort();
  }, [showSession, targetLocation]);

  const run = async action => {
    if (busy) return;
    setBusy(true); setError('');
    try { await action(); }
    catch (err) { setError(err.response?.data?.message || 'Die Änderung konnte nicht gespeichert werden.'); }
    finally { setBusy(false); }
  };
  const changed = async () => { await onChanged?.(); };
  const openLink = () => run(async () => {
    const response = await api.get(`/api/tattoo-projects/${project.id}/candidates`);
    setCandidates(response.data); setSelectedIds([]); setShowLink(true);
  });
  const field = (name, value) => setSession(previous => ({ ...previous, [name]: value }));

  return <>
    <Modal isOpen onRequestClose={() => { if (!busy && !viewerImages.length) onClose(); }}
      shouldCloseOnEsc={!busy && !viewerImages.length} shouldCloseOnOverlayClick={!busy}
      className="tattoo-project-dialog" overlayClassName="tattoo-project-overlay" contentLabel="Tattoo-Projekt">
      <button type="button" className="tattoo-project-close" disabled={busy} onClick={onClose} aria-label="Projekt schließen">✕</button>
      <h2>{project ? 'Tattoo-Projekt' : 'Termine als Projekt zusammenfassen'}</h2>
      <p className="tattoo-project-customer">{project?.clientName || appointment.clientName}</p>
      {error && <p role="alert" className="tattoo-project-error">{error}</p>}
      {loading ? <p>Projekt wird geladen …</p> : !project ? <form onSubmit={event => {
        event.preventDefault();
        run(async () => {
          const response = await api.post('/api/tattoo-projects', { appointmentId: appointment.id, title });
          accept(response.data); await changed();
        });
      }}>
        <p>Beschreibung und Bilder dieses Termins werden einmal im Projekt hinterlegt. Weitere Sessions verwenden sie automatisch.</p>
        <label>Projektname<input autoFocus required maxLength={200} value={title} onChange={event => setTitle(event.target.value)} placeholder="z. B. Full Sleeve rechter Arm" /></label>
        <button type="submit" disabled={busy}>{busy ? 'Wird angelegt …' : 'Projekt anlegen'}</button>
      </form> : <>
        <form onSubmit={event => { event.preventDefault(); run(async () => {
          const response = await api.put(`/api/tattoo-projects/${project.id}`, { title, description, revision: project.revision });
          accept(response.data); await changed();
        }); }}>
          <label>Projektname<input required maxLength={200} value={title} onChange={event => setTitle(event.target.value)} /></label>
          <label>Gemeinsame Beschreibung<textarea rows={4} value={description} onChange={event => setDescription(event.target.value)} /></label>
          <p className="tattoo-project-hint">Änderungen gelten für alle Sessions und sind für die zugeteilten Künstler sichtbar.</p>
          <button type="submit" disabled={busy || (title === project.title && description === (project.description || ''))}>Projekt speichern</button>
          {error && <button type="button" disabled={busy} onClick={() => run(async () => accept((await api.get(`/api/tattoo-projects/${project.id}`)).data))}>Projekt neu laden</button>}
        </form>
        <section><h3>Gemeinsame Bilder</h3>
          <AppointmentImageManager key={project.id} projectId={project.id} initialImages={project.images}
            legacyImageUrl={project.legacy_image_url || ''}
            onOpenImages={(images, index) => { setViewerImages(images); setCurrentImage(index); }}
            onImagesChange={images => { setProject(previous => ({ ...previous, images })); onChanged?.(); }} />
        </section>
        <section><h3>Sessions · {project.sessions.length}</h3>
          <ol className="tattoo-project-sessions">{project.sessions.map((item, index) => <li key={item.id}>
            <strong>Session {index + 1}/{project.sessions.length}</strong>
            <span>{dateLabel(item.day)} · {String(item.time || '').slice(0, 5)} · {item.artistName || 'Künstler'}</span>
            <small>{item.duration} Min. · {item.minRange}–{item.maxRange} € · Anzahlung {item.deposit} €{item.workflow_status === 'done' ? ' · Abgeschlossen' : ''}</small>
            {item.project_session_note && <p>{item.project_session_note}</p>}
          </li>)}</ol>
          <div className="tattoo-project-actions">
            <button type="button" disabled={busy} onClick={() => setShowSession(value => !value)}>Weitere Session planen</button>
            <button type="button" disabled={busy} onClick={openLink}>Vorhandene Termine zuordnen</button>
          </div>
        </section>
        {showSession && <section><h3>Neue Session</h3>
          <form onSubmit={event => { event.preventDefault(); run(async () => {
            const response = await api.post(`/api/tattoo-projects/${project.id}/sessions`, session);
            accept(response.data.project, false); setSession(emptySession); setShowSession(false); await changed();
          }); }}>
            <div className="tattoo-project-fields">
              <label>Standort<select value={targetLocation} onChange={event => setTargetLocation(event.target.value)}>{locations.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
              <label>Tag und Künstler<select required disabled={daysLoading} value={session.dayId} onChange={event => field('dayId', event.target.value)}>
                <option value="">{daysLoading ? 'Tage werden geladen …' : 'Tag auswählen'}</option>
                {days.map(day => <option key={day.dayId} value={day.dayId}>{dateLabel(String(day.day).slice(0, 10))} · {day.artistName}</option>)}
              </select></label>
              <label>Uhrzeit<input type="time" required value={session.time} onChange={event => field('time', event.target.value)} /></label>
              <label>Dauer in Minuten<input type="number" required min="1" max="1440" value={session.duration} onChange={event => field('duration', event.target.value)} /></label>
              <label>Mindestpreis (€)<input type="number" min="0" step="0.01" value={session.minRange} onChange={event => field('minRange', event.target.value)} /></label>
              <label>Höchstpreis (€)<input type="number" min={session.minRange || 0} step="0.01" value={session.maxRange} onChange={event => field('maxRange', event.target.value)} /></label>
              <label>Anzahlung (€)<input type="number" min="0" step="0.01" value={session.deposit} onChange={event => field('deposit', event.target.value)} /></label>
            </div>
            <label>Notiz nur für diese Session<textarea rows={2} value={session.note} onChange={event => field('note', event.target.value)} placeholder="z. B. Oberarm außen" /></label>
            {!daysLoading && !days.length && <p>Für diesen Standort sind keine zukünftigen Künstler-Tage verfügbar.</p>}
            <button type="submit" disabled={busy || !session.dayId}>Session speichern</button>
          </form>
        </section>}
        {showLink && <section><h3>Vorhandene Termine dieses Kunden</h3>
          <p>Nur die ausgewählten Termine werden zugeordnet. Abweichende Beschreibungen bleiben als Session-Notizen erhalten; Bilder werden gemeinsam angezeigt.</p>
          {!candidates.length && <p>Keine weiteren passenden Termine gefunden.</p>}
          {candidates.map(item => <label className="tattoo-project-candidate" key={item.id}>
            <input type="checkbox" checked={selectedIds.includes(item.id)} onChange={event => setSelectedIds(previous => event.target.checked ? [...previous, item.id] : previous.filter(id => id !== item.id))} />
            <span>{dateLabel(item.day)} · {String(item.time).slice(0, 5)} · {item.artistName}<small>{item.description}</small></span>
          </label>)}
          <button type="button" disabled={busy || !selectedIds.length} onClick={() => run(async () => {
            const response = await api.post(`/api/tattoo-projects/${project.id}/link`, { appointmentIds: selectedIds });
            accept(response.data, false); setShowLink(false); setSelectedIds([]); await changed();
          })}>Ausgewählte Termine zusammenfassen</button>
        </section>}
      </>}
    </Modal>
    <AppointmentImageViewer images={viewerImages} currentImage={currentImage} setCurrentImage={setCurrentImage} onClose={() => setViewerImages([])} onShare={onShare} />
  </>;
}
